import type { Objective } from "@/types/entities";

export type ObjectiveSortKey = "owner" | "year" | "type" | "progress";
export type SortDirection = "asc" | "desc";

const SORT_KEYS: readonly ObjectiveSortKey[] = ["owner", "year", "type", "progress"];

export function isObjectiveSortKey(value: unknown): value is ObjectiveSortKey {
  return typeof value === "string" && (SORT_KEYS as readonly string[]).includes(value);
}

function ownerLastName(obj: Objective): string {
  const name = obj.owner_name?.trim() ?? "";
  if (!name) return "";
  const parts = name.split(/\s+/);
  return parts[parts.length - 1]!.toLowerCase();
}

function typeRank(obj: Objective): number {
  return obj.type === "committed" ? 0 : 1;
}

function compareByKey(a: Objective, b: Objective, key: ObjectiveSortKey): number {
  switch (key) {
    case "owner": {
      const al = ownerLastName(a);
      const bl = ownerLastName(b);
      if (al === bl) return 0;
      return al < bl ? -1 : 1;
    }
    case "year":
      return (a.year ?? 0) - (b.year ?? 0);
    case "type":
      return typeRank(a) - typeRank(b);
    case "progress":
      // Null handling occurs in sortObjectives before the asc/desc flip.
      return (a.completion_percent ?? 0) - (b.completion_percent ?? 0);
  }
}

/**
 * Returns a new array sorted by the given key/direction. Null sort key returns
 * a stable copy in the original order. Null `completion_percent` always sorts
 * last regardless of direction (sentinel handling, applied before the asc/desc
 * flip).
 */
export function sortObjectives(
  objectives: Objective[],
  sortKey: ObjectiveSortKey | null,
  direction: SortDirection,
): Objective[] {
  if (!sortKey) return objectives.slice();

  const indexed = objectives.map((obj, idx) => ({ obj, idx }));

  indexed.sort((a, b) => {
    // Sentinel handling — applied BEFORE the asc/desc flip so unknowns always
    // sort to the end regardless of direction.
    if (sortKey === "progress") {
      const aNull = a.obj.completion_percent == null;
      const bNull = b.obj.completion_percent == null;
      if (aNull && bNull) return a.idx - b.idx;
      if (aNull) return 1;
      if (bNull) return -1;
    }
    if (sortKey === "owner") {
      const aEmpty = !ownerLastName(a.obj);
      const bEmpty = !ownerLastName(b.obj);
      if (aEmpty && bEmpty) return a.idx - b.idx;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
    }
    const cmp = compareByKey(a.obj, b.obj, sortKey);
    if (cmp !== 0) return direction === "asc" ? cmp : -cmp;
    return a.idx - b.idx;
  });

  return indexed.map((entry) => entry.obj);
}
